import type { PermissionRefusal, PermissionRefusalCode } from '@cwos/api-client';

/**
 * THE PRIVILEGE CEILING'S REFUSAL, RENDERED AS THE SERVER WROTE IT.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT "SOMETHING WENT WRONG"
 * ---------------------------------------------------------------------------
 * Every authority-granting door in the product — role composition, composed-role
 * assignment, coverage assignment, superuser designation — refuses through
 * `PrivilegeCeilingError`, and its 403 body carries a CLOSED vocabulary of
 * reasons for a stated purpose. The route's own comment says why:
 *
 *   > a ceiling refusal that said only "forbidden" would send an administrator
 *   > to guess, and the most common guess — "I must need more permissions" — is
 *   > the one that turns a correct refusal into a real escalation.
 *
 * The transport already lifts those into `ForbiddenError.refusals` as typed
 * entries. Collapsing them back into a generic apology at the screen would
 * throw away the work and reintroduce the escalation — so this module renders
 * each code as the sentence that names the actual remedy, and the codes that
 * have NO remedy say so.
 *
 * ---------------------------------------------------------------------------
 * `reserved_permission` IS THE ONE THAT MUST NOT LOOK LIKE A BUG
 * ---------------------------------------------------------------------------
 * A reserved key is a real key of the real catalog that the product declines to
 * hand out. It is not missing, not misspelled, and not something a wider grant
 * fixes. An administrator who reads "you don't have permission" for it will go
 * and ask for more authority, be given it, and be refused identically — which
 * is precisely the loop the closed vocabulary exists to prevent.
 */

/**
 * What a person should DO about each refusal — the half a generic message
 * cannot carry.
 *
 * Written as a `Record` over the closed code union rather than a `switch` with
 * a default, so a new code added to `PermissionRefusalCode` fails this file's
 * typecheck instead of falling into a catch-all sentence that would be wrong
 * for it. The `unrecognized_grantability` entry is the same idea one layer up:
 * the server's own escape hatch for a state ITS build does not know, and a
 * client that treated it as "reserved" would report a deployment skew as a
 * policy decision.
 */
const REMEDY: Readonly<Record<PermissionRefusalCode, string>> = Object.freeze({
  unknown_permission:
    'That is not a permission in this product. Nothing can be granted to make it work — check the key.',
  reserved_permission:
    'This permission is reserved: the product declines to hand it out by hand, to anybody. No wider grant and no role will change this answer.',
  unrecognized_grantability:
    'The server could not read whether this permission may be granted. That is a deployment problem, not a decision about your account — report it rather than asking for more access.',
  permission_not_held:
    'You do not hold this permission yourself, and you cannot confer authority you do not have.',
  scope_not_held:
    'You hold this permission, but not over the scope this would confer it at. A grant confined to one program does not license an organization-wide one.',
  superuser_tier_not_administrable:
    'Only a superuser may confer the superuser tier. It is deliberately not reachable from the permission system, so no permission grant will open it.',
  self_grant:
    'You may not grant authority to yourself, at any tier. Ask somebody else with the authority to do it.',
});

/** The server's own sentence, followed by what to do about it. */
export function refusalLine(refusal: PermissionRefusal): string {
  const remedy = REMEDY[refusal.code];
  const key = refusal.permissionKey === null ? '' : ` (${refusal.permissionKey})`;
  const scope =
    refusal.scope === null
      ? ''
      : ` at ${refusal.scope.scopeType}${refusal.scope.scopeId === null ? '' : ` ${refusal.scope.scopeId}`}`;
  return `${refusal.message}${key}${scope} — ${remedy}`;
}

/**
 * The refusals a rejected request carried, or `null` when it carried none.
 *
 * STRUCTURAL, NOT `instanceof`, for the reason `LoadFailure.httpStatusOf` gives:
 * this app throws from two unrelated error hierarchies and neither is
 * `instanceof` the other. `null` means the body did not carry the structure —
 * an ordinary 403 or a different failure entirely — which is not the same as a
 * ceiling refusal with no reasons, a thing the server cannot produce.
 */
export function ceilingRefusalsOf(err: unknown): readonly PermissionRefusal[] | null {
  if (typeof err !== 'object' || err === null) return null;
  const lifted = (err as { refusals?: unknown }).refusals;
  if (Array.isArray(lifted) && lifted.length > 0) return lifted as readonly PermissionRefusal[];
  const body = (err as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return null;
  const raw = (body as { refusals?: unknown }).refusals;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const parsed = raw.filter(
    (entry): entry is PermissionRefusal =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as { code?: unknown }).code === 'string' &&
      typeof (entry as { message?: unknown }).message === 'string' &&
      (entry as { code: string }).code in REMEDY,
  );
  return parsed.length > 0 ? parsed : null;
}

/**
 * The sentence to put in front of a person after a write was refused.
 *
 * `fallback` is used only when the failure carried no ceiling structure at all
 * — a 500, a dropped connection, a validation 400. A refusal that DID carry
 * structure never falls back, because the structured answer is strictly better
 * than any sentence this screen could write.
 */
export function writeRefusalMessage(err: unknown, fallback: string): string {
  const refusals = ceilingRefusalsOf(err);
  if (refusals !== null) {
    return `Refused, and here is exactly why. ${refusals.map(refusalLine).join(' ')}`;
  }
  if (typeof err === 'object' && err !== null) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return fallback;
}

/**
 * Did the refusal include a reserved key? The screens use this to say the one
 * thing an administrator most needs to hear: asking for more access will not
 * help, and the control they are looking at is not broken.
 */
export function refusedForReservedKey(err: unknown): boolean {
  return (ceilingRefusalsOf(err) ?? []).some((r) => r.code === 'reserved_permission');
}
